import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import ErrorBoundary from './components/ErrorBoundary';
import MessagingConsentWrapper from './components/MessagingConsentWrapper';
import AppLayout from './components/AppLayout';
import Landing from './pages/Landing';
import Demo from './pages/Demo';
import SignIn from './pages/SignIn';
import TwoFactorLogin from './pages/TwoFactorLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePasswordFirstLogin from './pages/ChangePasswordFirstLogin';
import CompanyRegister from './pages/CompanyRegister';
import Signup from './pages/Signup';
import CompanyLanding from './pages/CompanyLanding';
import ClockInScreen from './pages/ClockInScreen';
import KioskBoard from './pages/KioskBoard';
import SetupPayment from './pages/SetupPayment';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import HomePage from './pages/HomePage';
import StaffPage from './pages/StaffPage';
import PayrollPage from './pages/PayrollPage';
import PayRuns from './pages/PayRuns';
import Reimbursements from './pages/Reimbursements';
import InvoicesPage from './pages/InvoicesPage';
import LegacyRedirect from './components/nav/LegacyRedirect';
import SystemHealth from './pages/SystemHealth';
import StaffDashboard from './pages/StaffDashboard';
import MyTime from './pages/MyTime';
import Approvals from './pages/Approvals';
import SupervisorDashboard from './pages/SupervisorDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import JobsEnhanced from './pages/JobsEnhanced';
import ReportsEnhanced from './pages/ReportsEnhanced';
import Map from './pages/Map';
import Settings from './pages/Settings';
import SearchPage from './pages/SearchPage';
import AuditLog from './pages/AuditLog';
import CustomerPortal from './pages/CustomerPortal';
import CustomerAccountPortal from './pages/CustomerAccountPortal';
import TimeOff from './pages/TimeOff';
import JobsKanban from './pages/JobsKanban';
import Workplans from './pages/Workplans';
import Notes from './pages/Notes';
import Estimates from './pages/Estimates';
import EstimateBuilder from './pages/EstimateBuilder';
import PublicEstimate from './pages/PublicEstimate';
import Archive from './pages/Archive';
// Error Log page removed from tenant UI (Feb 2026) — errors still flow silently
// to the platform via /api/client-errors. Triage now lives in Platform HQ.
import Boards from './pages/Boards';
import BoardDetail from './pages/BoardDetail';
import UserManual from './pages/UserManual';
import Changelog from './pages/Changelog';

// Site QR deep-link (Aug 2026): posters encode /clock-in?qr=<token>. Capture
// the token before any auth redirect can strip the query string.
try {
  const _qsQr = new URLSearchParams(window.location.search).get('qr');
  if (_qsQr && window.location.pathname === '/clock-in') {
    sessionStorage.setItem('ht_pending_qr', _qsQr);
  }
} catch { /* noop */ }
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Security from './pages/Security';
import About from './pages/About';
import Contact from './pages/Contact';
import Support from './pages/Support';
import SupportChat from './components/SupportChat';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';

// Platform HQ (Scalevisory) — completely isolated from customer auth
import { PlatformAuthProvider } from './context/PlatformAuthContext';
import PlatformSignIn from './pages/PlatformSignIn';
import PlatformDashboard from './pages/PlatformDashboard';
import PlatformProfile from './pages/PlatformProfile';
import PlatformSupport from './pages/PlatformSupport';
import PlatformSmoke from './pages/PlatformSmoke';
import PlatformErrorLog from './pages/PlatformErrorLog';
import PlatformManual from './pages/PlatformManual';
import SecuritySetup from './pages/SecuritySetup';

// Wrapper for authenticated pages that use the sidebar layout
function WithLayout({ children }) {
  return <AppLayout>{children}</AppLayout>;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
      <BrowserRouter>
        <Routes>
          {/* Platform HQ — Scalevisory operating console.
              Mounted OUTSIDE of <AuthProvider> so platform admins never share
              tokens, contexts, or providers with customer (HinesTime) users. */}
          <Route
            path="/platform-admin/*"
            element={
              <PlatformAuthProvider>
                <ErrorBoundary>
                  <Routes>
                    <Route index element={<PlatformSignIn />} />
                    <Route path="signin" element={<PlatformSignIn />} />
                    <Route path="dashboard" element={<PlatformDashboard />} />
                    <Route path="profile" element={<PlatformProfile />} />
                    <Route path="support" element={<PlatformSupport />} />
                    <Route path="smoke" element={<PlatformSmoke />} />
                    <Route path="errors" element={<PlatformErrorLog />} />
                    <Route path="manual" element={<PlatformManual />} />
                  </Routes>
                  <Toaster />
                </ErrorBoundary>
              </PlatformAuthProvider>
            }
          />

          {/* Customer Portal — PUBLIC, token-authenticated job view.
              Mounted OUTSIDE <AuthProvider> so customers don't trigger any
              auth redirect / sign-in UI. The :token path param IS the
              credential; the backend resolves it server-side and 404s on
              an invalid/revoked link. Jun 2026. */}
          <Route
            path="/portal/account/:token"
            element={
              <ErrorBoundary>
                <CustomerAccountPortal />
                <Toaster />
              </ErrorBoundary>
            }
          />
          <Route
            path="/portal/:token"
            element={
              <ErrorBoundary>
                <CustomerPortal />
                <Toaster />
              </ErrorBoundary>
            }
          />

          {/* Everything else — regular HinesTime customer app */}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <MessagingConsentWrapper>
                  <ErrorBoundary>
                    <Routes>
                      {/* Public routes (no sidebar) */}
                      <Route path="/" element={<Landing />} />
                      <Route path="/demo" element={<Demo />} />
                      <Route path="/signin" element={<SignIn />} />
                      <Route path="/2fa-verify" element={<TwoFactorLogin />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/change-password-first-login" element={<ChangePasswordFirstLogin />} />
                      <Route path="/security-setup" element={<SecuritySetup />} />
                      <Route path="/register" element={<CompanyRegister />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/c/:slug" element={<CompanyLanding />} />
                      <Route path="/clock-in" element={<ClockInScreen />} />
                      <Route path="/kiosk/:token" element={<KioskBoard />} />
                      <Route path="/setup-payment" element={<SetupPayment />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/security" element={<Security />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/support" element={<Support />} />
                      <Route path="/user-manual" element={<UserManual />} />
                    <Route path="/whats-new" element={<Changelog />} />
                      <Route path="/estimate/:token" element={<PublicEstimate />} />

                      {/* Authenticated routes with sidebar layout */}
                      {/* Sep 2026 nav restructure: merged pages + redirects for every legacy route */}
                      <Route path="/home" element={<WithLayout><HomePage /></WithLayout>} />
                      <Route path="/dashboard" element={<LegacyRedirect to="/home" tab="company" />} />
                      <Route path="/dashboard/admin" element={<LegacyRedirect to="/home" tab="company" />} />
                      <Route path="/worker" element={<LegacyRedirect to="/home" tab="myday" />} />
                      <Route path="/staff" element={<WithLayout><StaffPage /></WithLayout>} />
                      <Route path="/manage-users" element={<LegacyRedirect to="/staff" tab="all" />} />
                      <Route path="/teams" element={<LegacyRedirect to="/staff" tab="my-team" />} />
                      <Route path="/manage-teams" element={<LegacyRedirect to="/staff" tab="teams" />} />
                      <Route path="/payroll" element={<WithLayout><PayrollPage /></WithLayout>} />
                      <Route path="/pay-runs" element={<WithLayout><PayRuns /></WithLayout>} />
                      <Route path="/reimbursements" element={<WithLayout><Reimbursements /></WithLayout>} />
                      <Route path="/payroll-review" element={<LegacyRedirect to="/payroll" tab="review" />} />
                      <Route path="/invoices" element={<WithLayout><InvoicesPage /></WithLayout>} />
                      <Route path="/daily-invoices" element={<LegacyRedirect to="/invoices" tab="daily" />} />
                      <Route path="/billing" element={<LegacyRedirect to="/invoices" tab="billing" />} />
                      <Route path="/customers" element={<LegacyRedirect to="/settings" section="customers" />} />
                      <Route path="/projects" element={<LegacyRedirect to="/settings" section="projects" />} />
                      <Route path="/dashboard/supervisor" element={<WithLayout><SupervisorDashboard /></WithLayout>} />
                      <Route path="/dashboard/staff" element={<StaffDashboard />} />
                      <Route path="/my-time" element={<MyTime />} />
                      <Route path="/approvals" element={<WithLayout><Approvals /></WithLayout>} />
                      <Route path="/admin" element={<SuperAdminDashboard />} />
                      <Route path="/jobs" element={<WithLayout><JobsEnhanced /></WithLayout>} />
                      <Route path="/map" element={<WithLayout><Map /></WithLayout>} />
                      <Route path="/reports" element={<WithLayout><ReportsEnhanced /></WithLayout>} />
                      <Route path="/settings" element={<WithLayout><Settings /></WithLayout>} />
                      <Route path="/search" element={<WithLayout><SearchPage /></WithLayout>} />
                      <Route path="/system-health" element={<WithLayout><SystemHealth /></WithLayout>} />
                      <Route path="/audit-log" element={<WithLayout><AuditLog /></WithLayout>} />
                      <Route path="/time-off" element={<WithLayout><TimeOff /></WithLayout>} />
                      <Route path="/jobs/board" element={<WithLayout><JobsKanban /></WithLayout>} />
                      <Route path="/workplans" element={<WithLayout><Workplans /></WithLayout>} />
                      <Route path="/notes" element={<WithLayout><Notes /></WithLayout>} />
                      <Route path="/boards" element={<WithLayout><Boards /></WithLayout>} />
                      <Route path="/boards/:boardId" element={<WithLayout><BoardDetail /></WithLayout>} />
                      <Route path="/estimates" element={<WithLayout><Estimates /></WithLayout>} />
                      <Route path="/estimates/new" element={<WithLayout><EstimateBuilder /></WithLayout>} />
                      <Route path="/estimates/:id" element={<WithLayout><EstimateBuilder /></WithLayout>} />
                      <Route path="/archive" element={<WithLayout><Archive /></WithLayout>} />
                      {/* Unknown paths (e.g. legacy /schedule) → home instead of a blank page */}
                      <Route path="*" element={<Navigate to="/home" replace />} />
                    </Routes>
                    {/* Inside the boundary (Aug 2026): a crash in SupportChat
                        or a toast render must show the friendly error screen,
                        not a blank page. */}
                    <SupportChat />
                    <Toaster />
                  </ErrorBoundary>
                </MessagingConsentWrapper>
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
      {/* sonner toasts (toast.success/error across the app) — must be
          mounted once at root or every sonner call is a silent no-op. */}
      <SonnerToaster theme="dark" richColors closeButton position="top-right" />
      </GoogleReCaptchaProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
